// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Container } from 'unstated';

import type { EmojiDescription, OptionalEmojiDescription } from '@atlaskit/emoji/types';

import { getEmojiId, getHasLiveDocsDefaultEmoji } from '../transformer.ts';
import type { EmojiStatusFieldName } from '../__types__/EmojiStatusFieldName.ts';

import type { getEmojiTitleProperty as getEmojiTitlePropertyType } from './__types__/getEmojiTitleProperty.ts';

interface EmojiTitleSSRData {
	emojiStatusFieldName: EmojiStatusFieldName;
	queryData: getEmojiTitlePropertyType;
	isLivePage: boolean;
}

export class EmojiTitleStateContainer extends Container<EmojiTitleState> {
	state = {
		emojiPickerLoaded: false,
		isLoading: true,
		isMouseEnterTitle: false,
		isUpdatingEmoji: false,
		openFirstEmojiPicker: false,
		openSecondEmojiPicker: false,
		selectedEmoji: null,
		isEmojiButtonFocused: false,
		isRemovingEmoji: false,
		hasLiveDocsDefaultEmoji: false,
	};

	constructor(ssrEmojiTitleData?: any) {
		super();
		if (ssrEmojiTitleData) {
			this.populateState(ssrEmojiTitleData as EmojiTitleSSRData);
		}
	}

	populateState(ssrEmojiTitleData: EmojiTitleSSRData): void {
		const { emojiStatusFieldName, queryData, isLivePage } = ssrEmojiTitleData as EmojiTitleSSRData;

		const emojiId = getEmojiId(queryData, emojiStatusFieldName);
		const hasDefaultLiveTitleEmoji = getHasLiveDocsDefaultEmoji(queryData);

		void this.setSelectedEmoji(emojiId || null);
		void this.setHasLiveDocsDefaultEmoji(hasDefaultLiveTitleEmoji && isLivePage);
	}

	onUpdateStart = (): Promise<void> => this.setState({ isUpdatingEmoji: true });
	onUpdateEnd = (): void => {
		void this.setState({ isUpdatingEmoji: false });
		void this.setState({ isRemovingEmoji: false });
	};
	setIsEmojiButtonFocused = (isEmojiButtonFocused: boolean): Promise<void> =>
		this.setState({ isEmojiButtonFocused });

	setIsRemovingEmoji = (isRemovingEmoji: boolean): Promise<void> =>
		this.setState({ isRemovingEmoji });
	setIsUpdatingEmoji = (isUpdatingEmoji: boolean): Promise<void> =>
		this.setState({ isUpdatingEmoji });

	// first emoji picker is open when user already selected an emoji
	// click on the emoji and want to change it to different emoji
	setOpenFirstEmojiPicker = (isOpen: boolean): void => {
		if (this.state.openFirstEmojiPicker !== isOpen) {
			void this.setState({ openFirstEmojiPicker: isOpen });
		}
	};

	// second emoji picker is open when not yet selected an emoji
	// and want to add emoji via Add emoji button
	setOpenSecondEmojiPicker = (isOpen: boolean): void => {
		if (this.state.openSecondEmojiPicker !== isOpen) {
			void this.setState({ openSecondEmojiPicker: isOpen });
		}
	};

	setSelectedEmoji = (
		emoji: OptionalEmojiDescription | EmojiDescription | string | null,
	): Promise<void> => this.setState({ selectedEmoji: emoji });

	onEmojiPickerLoaded = (): void => {
		if (!this.state.emojiPickerLoaded) {
			void this.setState({ emojiPickerLoaded: true });
		}
	};

	// for testing
	setContainerState = (newState: Partial<EmojiTitleState>): Promise<void> =>
		this.setState((state) => ({
			...state,
			...newState,
		}));

	setLoadingEmoji = (isLoading: EmojiTitleState['isLoading']): Promise<void> =>
		this.setState({ isLoading });

	setHasLiveDocsDefaultEmoji = (hasDefaultEmoji: boolean): Promise<void> =>
		this.setState({ hasLiveDocsDefaultEmoji: hasDefaultEmoji });
}

type EmojiTitleState = {
	emojiPickerLoaded: boolean;
	isLoading: boolean;
	isMouseEnterTitle: boolean;
	isUpdatingEmoji: boolean;
	isEmojiButtonFocused: boolean;
	openFirstEmojiPicker: boolean;
	openSecondEmojiPicker: boolean;
	selectedEmoji: OptionalEmojiDescription | EmojiDescription | string | null;
	isRemovingEmoji: boolean;
	hasLiveDocsDefaultEmoji: boolean;
};
